<template>
  <div class="dossier-view" :class="{ 'dossier-view--print': isPrintMode }">
    <!-- NavBar oculto no modo impressão -->
    <NavBar v-if="!isPrintMode" />
    <AuthBySessionExpired v-if="!isPrintMode" />

    <!-- ─── Barra de controles (oculta na impressão) ─── -->
    <div v-if="!isPrintMode" class="dossier-view__controls no-print">
      <div class="dossier-view__controls-left">
        <v-btn-previous-page />
        <div>
          <h1 class="dossier-view__title">
            <i class="fas fa-folder-open mr-2"></i>
            Dossiê Institucional
          </h1>
          <p class="dossier-view__subtitle">
            Visualização completa para gestão e fiscalização
          </p>
        </div>
      </div>

      <div class="dossier-view__controls-right">
        <!-- Filtro de seções -->
        <div class="dossier-view__section-filter" v-if="false">
          <v-select
            v-model="jumpToSection"
            :items="sectionOptions"
            dense
            label="Ir para:"
            outlined
            hide-details
            class="dossier-view__filter-select"
            @change="scrollToSection"
          />
        </div>

        <!-- Ocultar módulos N/A -->
        <label class="dossier-view__toggle-na" v-if="false">
          <input
            type="checkbox"
            v-model="hideNA"
            class="dossier-view__toggle-checkbox"
          />
          <span>Ocultar módulos não aplicáveis</span>
        </label>

        <!-- Ações -->
        <v-btn
          v-if="permission('read', 'dossier_edit')"
          class="btn-certified ml-2"
          @click="$router.push({ name: 'dossier-edit' })"
        >
          <i class="fas fa-pencil-alt mr-1"></i>
          Editar
        </v-btn>

        <v-btn class="btn-add-down ml-2" @click="printDossier">
          <i class="fas fa-print mr-1"></i>
          Imprimir
        </v-btn>
      </div>
    </div>

    <!-- ─── Loading ─── -->
    <b-overlay :show="loading" opacity="0.99">
      <div class="dossier-view__content" ref="dossierContent">
        <!-- ══════════════════════════════════════════════
             MÓDULO 1 — CAPA
             ══════════════════════════════════════════════ -->
        <div
          id="section-cover"
          class="dossier-view__module dossier-view__module--page-break"
        >
          <DossierCover mode="view" />
        </div>

        <!-- ══════════════════════════════════════════════
             MÓDULO 2 — IDENTIFICAÇÃO INSTITUCIONAL
             ══════════════════════════════════════════════ -->
        <div
          id="section-identification"
          class="dossier-view__module dossier-view__module--page-break"
        >
          <DossierIdentification mode="view" />
        </div>

        <!-- ══════════════════════════════════════════════
             GRUPO 2 — DOCUMENTOS
             ══════════════════════════════════════════════ -->
        <div class="dossier-view__group-header no-print" id="section-documents">
          <i class="fas fa-file-alt mr-2"></i>
          Documentos obrigatórios e condicionais
        </div>

        <!-- CNPJ -->
        <div
          id="section-cnpj"
          class="dossier-view__module"
          v-if="!shouldHide('cnpj')"
        >
          <DossierCnpj mode="view" :initial-data="modulesData.cnpj" />
        </div>

        <!-- Responsável Técnico -->
        <div
          id="section-technical_manager"
          class="dossier-view__module"
          v-if="
            !shouldHide('technical_manager') &&
            isSection('DossierTechnicalManager')
          "
        >
          <DossierTechnicalManager
            mode="view"
            :initial-data="modulesData.technical_manager"
          />
        </div>

        <!-- Alvará -->
        <div
          id="section-license"
          class="dossier-view__module"
          v-if="!shouldHide('license') && isSection('DossierLicense')"
        >
          <DossierLicense mode="view" :initial-data="modulesData.license" />
        </div>

        <!-- Licença Sanitária -->
        <div
          id="section-sanitary_license"
          class="dossier-view__module"
          v-if="
            !shouldHide('sanitary_license') &&
            isSection('DossierSanitaryLicense')
          "
        >
          <DossierSanitaryLicense
            mode="view"
            :initial-data="modulesData.sanitary_license"
          />
        </div>

        <!-- Termo de Visita Sanitária -->
        <div
          id="section-sanitary_visit"
          class="dossier-view__module"
          v-if="
            !shouldHide('sanitary_visit') && isSection('DossierSanitaryVisit')
          "
        >
          <DossierSanitaryVisit
            mode="view"
            :initial-data="modulesData.sanitary_visit"
          />
        </div>

        <!-- Corpo de Bombeiros -->
        <div
          id="section-fire_dept"
          class="dossier-view__module"
          v-if="!shouldHide('fire_dept') && isSection('DossierFireDept')"
        >
          <DossierFireDept mode="view" :initial-data="modulesData.fire_dept" />
        </div>

        <!-- CMI / CMDPI -->
        <div
          id="section-elderly_council"
          class="dossier-view__module"
          v-if="
            !shouldHide('elderly_council') && isSection('DossierElderlyCouncil')
          "
        >
          <DossierElderlyCouncil
            mode="view"
            :initial-data="modulesData.elderly_council"
          />
        </div>

        <!-- CMAS (condicional) -->
        <div
          id="section-welfare_council"
          class="dossier-view__module"
          v-if="
            !shouldHide('welfare_council') && isSection('DossierWelfareCouncil')
          "
        >
          <DossierWelfareCouncil
            mode="view"
            :initial-data="modulesData.welfare_council"
          />
        </div>

        <!-- Reservatórios de Água -->
        <div
          id="section-water_tank"
          class="dossier-view__module"
          v-if="!shouldHide('water_tank') && isSection('DossierWaterTank')"
        >
          <DossierWaterTank
            mode="view"
            :initial-data="modulesData.water_tank"
          />
        </div>

        <!-- Potabilidade (condicional) -->
        <div
          id="section-water_potability"
          class="dossier-view__module"
          v-if="
            !shouldHide('water_potability') &&
            isSection('DossierWaterPotability')
          "
        >
          <DossierWaterPotability
            mode="view"
            :initial-data="modulesData.water_potability"
          />
        </div>

        <!-- Controle de Pragas -->
        <div
          id="section-pest_control"
          class="dossier-view__module"
          v-if="!shouldHide('pest_control') && isSection('DossierPestControl')"
        >
          <DossierPestControl
            mode="view"
            :initial-data="modulesData.pest_control"
          />
        </div>

        <!-- Terceirização (condicional) -->
        <div
          id="section-outsourcing"
          class="dossier-view__module"
          v-if="!shouldHide('outsourcing') && isSection('DossierOutsourcing')"
        >
          <DossierOutsourcing
            mode="view"
            :initial-data="modulesData.outsourcing"
          />
        </div>

        <!-- Prestadores de Saúde (condicional) -->
        <div
          id="section-health_providers"
          class="dossier-view__module"
          v-if="
            !shouldHide('health_providers') &&
            isSection('DossierHealthProviders')
          "
        >
          <DossierHealthProviders
            mode="view"
            :initial-data="modulesData.health_providers"
          />
        </div>

        <!-- RH – CLT -->
        <div
          id="section-hr_clt"
          class="dossier-view__module"
          v-if="!shouldHide('hr_clt') && isSection('DossierHRClt')"
        >
          <DossierHRClt mode="view" :initial-data="modulesData.hr_clt" />
        </div>

        <!-- Infraestrutura -->
        <div
          id="section-infrastructure"
          class="dossier-view__module"
          v-if="
            !shouldHide('infrastructure') && isSection('DossierInfrastructure')
          "
        >
          <DossierInfrastructure
            mode="view"
            :initial-data="modulesData.infrastructure"
          />
        </div>

        <!-- POPs -->
        <div
          id="section-sops"
          class="dossier-view__module"
          v-if="!shouldHide('sops') && isSection('DossierSOPs')"
        >
          <DossierSOPs mode="view" :initial-data="modulesData.sops" />
        </div>

        <!-- ══════════════════════════════════════════════
             GRUPO 3 — MÓDULOS OPERACIONAIS
             ══════════════════════════════════════════════ -->
        <div
          v-if="false"
          class="dossier-view__group-header no-print"
          id="section-operational"
        >
          <i class="fas fa-calendar-alt mr-2"></i>
          Módulos operacionais
        </div>

        <!-- Escala de Trabalho — apenas vigente -->
        <div
          id="section-work_schedule"
          class="dossier-view__module dossier-view__module--page-break"
          v-if="
            (activeWorkSchedules.length > 0 || !hideNA) &&
            isSection('DossierWorkSchedule')
          "
        >
          <div class="dossier-view__operational-title">
            <i class="fas fa-calendar-alt mr-2"></i>
            Escala de Trabalho
            <span class="dossier-view__operational-subtitle">
              — exibindo apenas escalas vigentes
            </span>
          </div>

          <div v-if="activeWorkSchedules.length > 0">
            <div
              v-for="schedule in activeWorkSchedules"
              :key="schedule.id"
              class="dossier-view__operational-card"
            >
              <div class="dossier-view__op-card-header">
                <span class="dossier-view__op-card-name">{{
                  schedule.name
                }}</span>
                <span class="dossier-view__op-type-pill">{{
                  schedule.type
                }}</span>
              </div>
              <div class="dossier-view__op-card-meta">
                <i class="fas fa-calendar-alt mr-1"></i>
                {{ formatDateBR(schedule.start_date) }} →
                {{ formatDateBR(schedule.end_date) }}
                <span v-if="schedule.responsible_name" class="ml-3">
                  <i class="fas fa-user mr-1"></i
                  >{{ schedule.responsible_name }}
                </span>
              </div>
              <div
                v-for="(file, idx) in schedule.files || []"
                :key="idx"
                class="dossier-view__op-file"
              >
                <i class="fas fa-file-pdf dossier-view__op-file-icon"></i>
                <span>{{ file.name || `Arquivo ${idx + 1}` }}</span>
                <a
                  :href="file.url"
                  target="_blank"
                  class="dossier-view__op-file-link ml-2"
                >
                  <i class="fas fa-eye mr-1"></i>Ver
                </a>
              </div>
              <div
                v-if="!schedule.files || schedule.files.length === 0"
                class="dossier-view__op-no-file"
              >
                <i class="fas fa-exclamation-circle mr-1"></i>Nenhum arquivo
                anexado
              </div>
            </div>
          </div>
          <div v-else class="dossier-view__op-empty">
            <i class="fas fa-calendar-alt mr-1"></i>
            Nenhuma escala vigente cadastrada
          </div>
        </div>

        <!-- Cardápio — apenas vigente -->
        <div
          id="section-menu"
          class="dossier-view__module dossier-view__module--page-break"
          v-if="(activeMenus.length > 0 || !hideNA) && isSection('DossierMenu')"
        >
          <div class="dossier-view__operational-title">
            <i class="fas fa-utensils mr-2"></i>
            Cardápio
            <span class="dossier-view__operational-subtitle">
              — exibindo apenas cardápios vigentes
            </span>
          </div>

          <div v-if="activeMenus.length > 0">
            <div
              v-for="menu in activeMenus"
              :key="menu.id"
              class="dossier-view__operational-card"
            >
              <div class="dossier-view__op-card-header">
                <span class="dossier-view__op-card-name">{{ menu.name }}</span>
                <span class="dossier-view__op-type-pill">{{ menu.type }}</span>
              </div>
              <div class="dossier-view__op-card-meta">
                <i class="fas fa-calendar-alt mr-1"></i>
                {{ formatDateBR(menu.start_date) }} →
                {{ formatDateBR(menu.end_date) }}
                <span v-if="menu.responsible_name" class="ml-3">
                  <i class="fas fa-user mr-1"></i>{{ menu.responsible_name }}
                </span>
              </div>
              <div
                v-for="(file, idx) in menu.files || []"
                :key="idx"
                class="dossier-view__op-file"
              >
                <i class="fas fa-file-pdf dossier-view__op-file-icon"></i>
                <span>{{ file.name || `Arquivo ${idx + 1}` }}</span>
                <a
                  :href="file.url"
                  target="_blank"
                  class="dossier-view__op-file-link ml-2"
                >
                  <i class="fas fa-eye mr-1"></i>Ver
                </a>
              </div>
              <div
                v-if="!menu.files || menu.files.length === 0"
                class="dossier-view__op-no-file"
              >
                <i class="fas fa-exclamation-circle mr-1"></i>Nenhum arquivo
                anexado
              </div>
            </div>
          </div>
          <div v-else class="dossier-view__op-empty">
            <i class="fas fa-utensils mr-1"></i>
            Nenhum cardápio vigente cadastrado
          </div>
        </div>

        <!-- ─── Rodapé do Dossiê ─── -->
        <div class="dossier-view__dossier-footer">
          <p class="dossier-view__footer-text">
            Dossiê gerado pelo Sistema Clin em {{ generatedAt }}. Este documento
            é de uso interno e para apresentação à fiscalização. O Sistema Clin
            não valida o conteúdo jurídico ou técnico dos documentos
            registrados.
          </p>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import NavBar from "../../templates/NavBar";
import AuthBySessionExpired from "../../templates/Components/AuthBySessionExpired";
import VBtnPreviousPage from "../../templates/Components/VBtnPreviousPage.vue";

// Módulos do Dossiê
import DossierCover from "../../components/modules/DossierCover.vue";
import DossierIdentification from "../../components/modules/DossierIdentification.vue";
import DossierCnpj from "../../components/modules/DossierCnpj.vue";
import DossierTechnicalManager from "../../components/modules/DossierTechnicalManager.vue";
import DossierLicense from "../../components/modules/DossierLicense.vue";
import DossierSanitaryLicense from "../../components/modules/DossierSanitaryLicense.vue";
import DossierSanitaryVisit from "../../components/modules/DossierSanitaryVisit.vue";
import DossierFireDept from "../../components/modules/DossierFireDept.vue";
import DossierElderlyCouncil from "../../components/modules/DossierElderlyCouncil.vue";
import DossierWelfareCouncil from "../../components/modules/DossierWelfareCouncil.vue";
import DossierWaterTank from "../../components/modules/DossierWaterTank.vue";
import DossierWaterPotability from "../../components/modules/DossierWaterPotability.vue";
import DossierPestControl from "../../components/modules/DossierPestControl.vue";
import DossierOutsourcing from "../../components/modules/DossierOutsourcing.vue";
import DossierHealthProviders from "../../components/modules/DossierHealthProviders.vue";
import DossierHRClt from "../../components/modules/DossierHR.vue";
import DossierInfrastructure from "../../components/modules/DossierInfrastructure.vue";
import DossierSOPs from "../../components/modules/DossierSOPs.vue";

import api from "../../services/api";
import permission from "../../lib/permission";

const DEFAULT_INFORMATIONS = [
  {
    key: "DossierTechnicalManager",
    label: "Responsável Técnico",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierLicense",
    label: "Alvará / Licença de Funcionamento",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierSanitaryLicense",
    label: "Licença Sanitária",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierSanitaryVisit",
    label: "Termo de Visita Sanitária",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierFireDept",
    label: "Certificado do Corpo de Bombeiros",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierElderlyCouncil",
    label: "Inscrição no Conselho Municipal da Pessoa Idosa (CMI / CMDPI)",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWelfareCouncil",
    label: "Registro no Conselho Municipal de Assistência Social (CMAS)",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWaterTank",
    label: "Limpeza dos Reservatórios de Água",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWaterPotability",
    label: "Laudo de Potabilidade da Água",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierPestControl",
    label: "Controle de Pragas e Vetores",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierOutsourcing",
    label: "Terceirização de Serviços",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierHealthProviders",
    label: "Prestadores de Saúde",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierHRClt",
    label: "Recursos Humanos – Colaboradores CLT",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierInfrastructure",
    label: "Infraestrutura da ILPI",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierSOPs",
    label: "POPs – Procedimentos Operacionais Padrão",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierWorkSchedule",
    label: "Escalas de Trabalho",
    selected: true,
    expanded: false,
    fields: [],
  },
  {
    key: "DossierMenu",
    label: "Cardápios",
    selected: true,
    expanded: false,
    fields: [],
  },
];

// ─── Mapa de seções para o jump-to ────────────────────────────────────────────
const SECTIONS = [
  { value: "cover", text: "Capa" },
  { value: "identification", text: "Identificação Institucional" },
  { value: "documents", text: "── Documentos ──" },
  { value: "cnpj", text: "CNPJ" },
  { value: "technical_manager", text: "Responsável Técnico" },
  { value: "license", text: "Alvará" },
  { value: "sanitary_license", text: "Licença Sanitária" },
  { value: "sanitary_visit", text: "Termo de Visita Sanitária" },
  { value: "fire_dept", text: "Corpo de Bombeiros" },
  { value: "elderly_council", text: "CMI / CMDPI" },
  { value: "welfare_council", text: "CMAS" },
  { value: "water_tank", text: "Reservatórios de Água" },
  { value: "water_potability", text: "Potabilidade da Água" },
  { value: "pest_control", text: "Controle de Pragas" },
  { value: "outsourcing", text: "Terceirização" },
  { value: "health_providers", text: "Prestadores de Saúde" },
  { value: "hr_clt", text: "RH – Colaboradores CLT" },
  { value: "infrastructure", text: "Infraestrutura" },
  { value: "sops", text: "POPs" },
  { value: "operational", text: "── Operacionais ──" },
  { value: "work_schedule", text: "Escala de Trabalho" },
  { value: "menu", text: "Cardápio" },
];

export default {
  name: "DossierView",

  components: {
    NavBar,
    AuthBySessionExpired,
    VBtnPreviousPage,
    DossierCover,
    DossierIdentification,
    DossierCnpj,
    DossierTechnicalManager,
    DossierLicense,
    DossierSanitaryLicense,
    DossierSanitaryVisit,
    DossierFireDept,
    DossierElderlyCouncil,
    DossierWelfareCouncil,
    DossierWaterTank,
    DossierWaterPotability,
    DossierPestControl,
    DossierOutsourcing,
    DossierHealthProviders,
    DossierHRClt,
    DossierInfrastructure,
    DossierSOPs,
  },

  data() {
    return {
      loading: true,
      isPrintMode: false,

      // Dados de todos os módulos
      modulesData: {},

      // Módulos operacionais (vigentes calculados no computed)
      workSchedules: [],
      menus: [],

      // Controles UI
      hideNA: false,
      jumpToSection: null,
      sectionOptions: SECTIONS,
      generatedAt: "",

      configMap: {},
    };
  },

  computed: {
    today() {
      return new Date().toISOString().split("T")[0];
    },

    // Somente escalas e cardápios com vigência ativa
    activeWorkSchedules() {
      return this.workSchedules.filter(
        (s) => s.start_date <= this.today && s.end_date >= this.today
      );
    },

    activeMenus() {
      return this.menus.filter(
        (m) => m.start_date <= this.today && m.end_date >= this.today
      );
    },
  },

  created() {
    this.generatedAt = this.formatDateTime(new Date());
    this.retrieve();
  },

  methods: {
    permission,

    // ── Busca todos os dados em paralelo ──────────────────────────────────
    async retrieve() {
      this.loading = true;

      try {
        const res = await api.get("/rest-home/dossier/full", { force: true });
        if (res.data && res.data.data) {
          this.modulesData = res.data.data;
        }
      } catch (err) {
        console.error("retrieve: erro no /full →", err);
      }

      try {
        const res = await api.get("/rest-home/dossier/work-schedule", {
          force: true,
        });
        this.workSchedules = res.data?.items || [];
      } catch (err) {
        console.error("retrieve: erro no /work-schedule →", err);
        this.workSchedules = [];
      }

      try {
        const res = await api.get("/rest-home/dossier/menu", { force: true });
        this.menus = res.data?.items || [];
      } catch (err) {
        console.error("retrieve: erro no /menu →", err);
        this.menus = [];
      }

      try {
        const res = await api.get("/rest-home/settings/config-dossier");

        if (!res.data.error) {
          let saved = res.data.informations;

          if (saved) {
            if (typeof saved === "string") {
              saved = JSON.parse(saved);
              if (typeof saved === "string") saved = JSON.parse(saved);
            }

            const merged = this.mergeSections(DEFAULT_INFORMATIONS, saved);
            this.configMap = this.buildConfigMap(merged);
          } else {
            this.configMap = this.buildConfigMap(DEFAULT_INFORMATIONS);
          }
        } else {
          this.configMap = this.buildConfigMap(DEFAULT_INFORMATIONS);
        }
      } catch (err) {
        this.configMap = this.buildConfigMap(DEFAULT_INFORMATIONS);
        console.error("retrieve: erro no /menu →", err);
      }

      this.loading = false;
    },

    buildConfigMap(sections) {
      const map = {};
      sections.forEach((section) => {
        const fieldsMap = {};
        if (section.fields) {
          section.fields.forEach((f) => {
            fieldsMap[f.key] = f.selected;
          });
        }
        map[section.key] = {
          selected: section.selected,
          fields: fieldsMap,
        };
      });
      return map;
    },

    mergeSections(defaults, saved) {
      return defaults.map((defaultSection) => {
        const savedSection = saved.find((s) => s.key === defaultSection.key);
        if (!savedSection) return { ...defaultSection };

        const mergedFields = defaultSection.fields.map((defaultField) => {
          const savedField = savedSection.fields
            ? savedSection.fields.find((f) => f.key === defaultField.key)
            : null;
          return savedField
            ? { ...defaultField, selected: savedField.selected }
            : { ...defaultField };
        });

        return {
          ...defaultSection,
          selected: savedSection.selected,
          fields: mergedFields,
        };
      });
    },

    isSection(sectionKey) {
      if (!this.configMap[sectionKey]) return true; // sem config → exibe
      return this.configMap[sectionKey].selected === true;
    },

    isField(sectionKey, fieldKey) {
      if (!this.isSection(sectionKey)) return false;
      const section = this.configMap[sectionKey];
      if (!section || section.fields[fieldKey] === undefined) return true; // sem config → exibe
      return section.fields[fieldKey] === true;
    },

    // ── Decidir se oculta módulo ──────────────────────────────────────────
    // Oculta quando:
    // 1. hideNA = true E módulo está marcado como não aplicável
    shouldHide(moduleKey) {
      if (!this.hideNA) return false;
      const data = this.modulesData[moduleKey];
      return data && data.not_applicable === true;
    },

    // ── Scroll para seção ─────────────────────────────────────────────────
    scrollToSection(sectionKey) {
      if (!sectionKey) return;
      const el = document.getElementById(`section-${sectionKey}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // Reset do select após navegar
      // this.$nextTick(() => {
      //   this.jumpToSection = null;
      // });
    },

    // ── Impressão via browser ─────────────────────────────────────────────
    printDossier() {
      this.isPrintMode = true;
      this.$nextTick(() => {
        window.print();
        this.isPrintMode = false;
      });
    },

    // ── Utilitários ────────────────────────────────────────────────────────
    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },

    formatDateTime(date) {
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${pad(date.getDate())}/${pad(
          date.getMonth() + 1
        )}/${date.getFullYear()} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
          date.getSeconds()
        )}`
      );
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER VIEW — Visualização completa para fiscalização
   ═══════════════════════════════════════════════════════════════ */

.dossier-view {
  min-height: 100vh;
  background: #f0f0ef;
}

/* ── Barra de controles ──────────────────────────────────────── */
.dossier-view__controls {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  border-bottom: 2px solid #1f5a76;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.dossier-view__controls-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dossier-view__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f5a76;
  margin: 0 0 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.dossier-view__subtitle {
  font-size: 11px;
  color: #888;
  margin: 0;
}

.dossier-view__controls-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

/* ── Filtro de seção (jump to) ───────────────────────────────── */
.dossier-view__section-filter {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dossier-view__filter-label {
  font-size: 12px;
  color: #555;
  white-space: nowrap;
}

.dossier-view__filter-select {
  width: 220px;
  font-size: 12px;
}

/* ── Toggle ocultar N/A ──────────────────────────────────────── */
.dossier-view__toggle-na {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
  cursor: pointer;
  white-space: nowrap;
}

.dossier-view__toggle-checkbox {
  accent-color: #1f5a76;
  width: 14px;
  height: 14px;
  cursor: pointer;
}

/* ── Conteúdo principal ──────────────────────────────────────── */
.dossier-view__content {
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 20px 60px;
}

/* ── Módulo individual ───────────────────────────────────────── */
.dossier-view__module {
  margin-bottom: 16px;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

/* Todos os DossierDocumentCards dentro do view ficam expandidos */
.dossier-view__module :deep(.dossier-card) {
  box-shadow: none;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
}

.dossier-view__module :deep(.dossier-card:last-child) {
  border-bottom: none;
}

/* ── Cabeçalho de grupo ──────────────────────────────────────── */
.dossier-view__group-header {
  font-size: 11px;
  font-weight: 700;
  color: #1f5a76;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 8px 0 6px;
  margin: 20px 0 8px;
  border-bottom: 2px solid #1f5a76;
}

/* ── Módulos operacionais ────────────────────────────────────── */
.dossier-view__operational-title {
  font-size: 14px;
  font-weight: 600;
  color: #33495e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #f8f8f7;
}

.dossier-view__operational-subtitle {
  font-size: 11px;
  font-weight: 400;
  color: #888;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 4px;
}

.dossier-view__operational-card {
  padding: 12px 16px;
  border-bottom: 1px solid #f4f4f4;
}

.dossier-view__operational-card:last-child {
  border-bottom: none;
}

.dossier-view__op-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.dossier-view__op-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.dossier-view__op-type-pill {
  font-size: 11px;
  font-weight: 600;
  background: #e8f4fd;
  color: #1f5a76;
  border: 1px solid #b8d9ec;
  border-radius: 10px;
  padding: 1px 8px;
}

.dossier-view__op-card-meta {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.dossier-view__op-file {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #333;
  padding: 4px 0;
}

.dossier-view__op-file-icon {
  color: #dc3545;
  font-size: 14px;
}
.dossier-view__op-file-link {
  color: #1f5a76;
  font-weight: 600;
}

.dossier-view__op-no-file {
  font-size: 12px;
  color: #aaa;
  font-style: italic;
}

.dossier-view__op-empty {
  padding: 20px 16px;
  text-align: center;
  font-size: 13px;
  color: #aaa;
  font-style: italic;
}

/* ── Rodapé do Dossiê ────────────────────────────────────────── */
.dossier-view__dossier-footer {
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  text-align: center;
}

.dossier-view__footer-text {
  font-size: 11px;
  color: #aaa;
  margin: 0;
  line-height: 1.6;
}

/* ═══════════════════════════════════════════════════════════════
   MODO IMPRESSÃO
   ═══════════════════════════════════════════════════════════════ */

/* Quebra de página entre módulos principais */
.dossier-view__module--page-break {
  page-break-after: always;
  break-after: page;
}

@media print {
  /* Remove elementos de UI */
  .no-print {
    display: none !important;
  }

  /* Remove sombras e bordas extras na impressão */
  .dossier-view {
    background: #fff;
  }
  .dossier-view__content {
    padding: 0;
    max-width: 100%;
  }
  .dossier-view__module {
    box-shadow: none;
    border: none;
    margin-bottom: 0;
  }

  /* Garante que os PDFs inline do DossierDocumentCard apareçam na impressão */
  .dossier-view__module :deep(.dossier-card__pdf-iframe) {
    height: 700px;
  }

  /* Rodapé */
  .dossier-view__dossier-footer {
    margin-top: 16px;
  }
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 768px) {
  .dossier-view__controls {
    flex-direction: column;
    align-items: flex-start;
    position: static; /* remove sticky em mobile */
  }

  .dossier-view__controls-right {
    width: 100%;
    flex-wrap: wrap;
  }

  .dossier-view__filter-select {
    width: 100%;
  }

  .dossier-view__content {
    padding: 12px 10px 40px;
  }
}
</style>